import React, { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, ChevronDown, ChevronUp, LineChart, MessageCircle, X } from 'lucide-react';

// import ChatBot from './ChatBot'; // Comentamos temporalmente la importación
import { consentManager } from '../utils/consentManager';

const FloatingButtons: React.FC = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [showDiagnosticPopup, setShowDiagnosticPopup] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // const [showChat, setShowChat] = useState(false); // Comentamos estados del chat
  // const [isChatMinimized, setIsChatMinimized] = useState(false);

  // Actualizar el efecto de scroll para mostrar/ocultar ambos botones
  useEffect(() => {
    const handleScrollVisibility = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      setShowScrollTop(scrollY > 400);
      setShowScrollBottom(documentHeight - (scrollY + windowHeight) > 400);
    };

    window.addEventListener('scroll', handleScrollVisibility);
    handleScrollVisibility(); // Llamada inicial
    return () => window.removeEventListener('scroll', handleScrollVisibility);
  }, []);

  // Inicialización y control del pop-up con consentimiento
  useEffect(() => {
    const checkConsentAndShowPopup = () => {
      const currentConsent = consentManager.getConsentStatus();
      const hasConsent = currentConsent.analytics || currentConsent.advertising;
      const wasShown = localStorage.getItem('diagnosticPopupShown');

      if (!wasShown && hasConsent) {
        setTimeout(() => {
          setShowDiagnosticPopup(true);
          localStorage.setItem('diagnosticPopupShown', 'true');
        }, 2000); // Retraso para asegurar que el banner de consentimiento se muestre primero
      }
    };

    // Suscribirse a cambios en el consentimiento
    const unsubscribe = consentManager.onConsentChange(checkConsentAndShowPopup);

    // Verificar estado inicial
    checkConsentAndShowPopup();

    return () => {
      unsubscribe();
    };
  }, []);

  const handleWhatsAppClick = () => {
    const message =
      '👋 *¡Hola! Me interesa conocer más sobre sus servicios*\n\n' +
      'Me gustaría recibir información sobre:\n' +
      '✓ Soluciones empresariales\n' +
      '✓ Optimización de procesos\n' +
      '✓ Transformación digital\n\n' +
      '¿Podrían asesorarme?';

    window.open(`https://wa.me/56984918725?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDiagnosticClick = () => {
    window.open(
      'https://docs.google.com/forms/d/e/1FAIpQLSe6Hyn1LcxayvpNBAd5QkqSA6tls2kw0YkY3FbUQGdrD-l-Hg/viewform',
      '_blank'
    );
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDiagnosticPopup(false);
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  const handleScroll = (direction: 'top' | 'bottom') => {
    if (direction === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {/* Chatbot Component - Temporalmente deshabilitado
      <ChatBot
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        onMinimize={() => setIsChatMinimized(!isChatMinimized)}
        isMinimized={isChatMinimized}
        position="right"
      /> */}

      {/* Botones flotantes */}
      <div className="fixed right-6 bottom-6 z-20">
        <div className="flex flex-col gap-3">
          {/* Botón del Chatbot - Temporalmente deshabilitado
          {!showChat && (
            <button
              onClick={() => setShowChat(true)}
              className="bg-[#003366] text-white p-4 rounded-full shadow-lg hover:bg-[#002244] transition-colors relative group"
              aria-label="Abrir chat"
            >
              <MessageCircle className="h-6 w-6" />
              <span className="absolute right-full mr-3 bg-[#003366] text-white px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Asistente Virtual
              </span>
            </button>
          )} */}

          {/* Botón de WhatsApp */}
          <button
            onClick={handleWhatsAppClick}
            className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors relative group"
            aria-label="Contactar por WhatsApp"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute right-full mr-3 bg-green-500 text-white px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              WhatsApp
            </span>
          </button>

          {/* Botones de scroll */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.button
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                onClick={() => handleScroll('top')}
                className="bg-gray-800 text-white p-4 rounded-full shadow-lg hover:bg-gray-700 transition-colors relative group"
                aria-label="Volver arriba"
              >
                <ChevronUp className="h-6 w-6" />
                <span className="absolute right-full mr-3 bg-gray-800 text-white px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Volver arriba
                </span>
              </motion.button>
            )}

            {showScrollBottom && (
              <motion.button
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                onClick={() => handleScroll('bottom')}
                className="bg-gray-800 text-white p-4 rounded-full shadow-lg hover:bg-gray-700 transition-colors relative group"
                aria-label="Ir abajo"
              >
                <ChevronDown className="h-6 w-6" />
                <span className="absolute right-full mr-3 bg-gray-800 text-white px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Ir al final
                </span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Pop-up de Diagnóstico Empresarial */}
      <AnimatePresence>
        {showDiagnosticPopup && (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{
              opacity: 1,
              x: 0,
              scale: isMinimized ? 0.95 : 1,
            }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ type: 'spring', damping: 25 }}
            className={`fixed bottom-6 left-6 z-30 bg-white rounded-[20px] shadow-2xl overflow-hidden
              ${isMinimized ? 'w-[200px]' : 'w-[95vw] max-w-[300px]'}`}
          >
            {/* Botones de control */}
            <div className="absolute top-2 right-2 flex items-center space-x-1">
              <button onClick={handleMinimize} className="text-gray-400 hover:text-gray-600 p-1">
                {isMinimized ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {!isMinimized ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-blue-500 w-10 h-10 rounded-xl flex items-center justify-center">
                      <LineChart className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">
                      ¡Diagnóstico Empresarial Gratuito!
                    </h3>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">
                    Obtén un análisis completo de tu negocio y descubre oportunidades de mejora.
                  </p>

                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="text-green-500">✓</div>
                      <span className="text-gray-600">Evaluación personalizada</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-green-500">✓</div>
                      <span className="text-gray-600">Recomendaciones estratégicas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-green-500">✓</div>
                      <span className="text-gray-600">Plan de acción detallado</span>
                    </div>
                  </div>

                  <button
                    onClick={handleDiagnosticClick}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-xl text-sm font-semibold transition-colors duration-200 flex items-center justify-center"
                  >
                    Obtener Diagnóstico Gratuito
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              ) : (
                // Versión minimizada
                <div className="flex items-center gap-2" onClick={() => setIsMinimized(false)}>
                  <div className="bg-blue-500 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LineChart className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-semibold text-gray-900 truncate">
                      Diagnóstico Empresarial
                    </h3>
                    <p className="text-xs text-gray-500 truncate">¡Análisis gratuito!</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingButtons;
